/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */

import type { Context, Value } from 'formulascript/interpreter';

import { wrapFunction } from '../utils';

type Table = Value[][];

export const DATASET = async (
  ctx: Context,
  count: number,
  ...columns: any[]
): Promise<Table> => {
  if (count < 0) {
    throw new Error('DATASET expects a row count of at least 0');
  }

  if (columns.length % 4 !== 0) {
    throw new Error('DATASET expects columns in sets of 4');
  }

  // The columns are sets of 4 values:
  // 1. The current value name
  // 2. The initial value
  // 3. The next value name
  // 4. The next value function (lambda)

  // Get the initial values for the columns, this is also the first row.
  const initialValues = await Promise.all(
    columns.filter((_, i) => {
      return i % 4 === 1;
    }),
  );

  const currentValueNames = columns.filter((_, i) => {
    return i % 4 === 0;
  });

  const nextValueNames = columns.filter((_, i) => {
    return i % 4 === 2;
  });

  const nextValueFunctions = columns.filter((_, i) => {
    return i % 4 === 3;
  });

  const table: Table = [];

  const initials = [];

  for (let i = 0; i < initialValues.length; i++) {
    const initialValue =
      typeof initialValues[i] === 'function'
        ? await initialValues[i](ctx, [])
        : initialValues[i];

    ctx.store(currentValueNames[i], initialValue);
    initials.push(initialValue);
  }

  table.push(initials);

  for (let i = 1; i < count; i++) {
    // First clean up the context from the previous row.
    currentValueNames.forEach((name) => {
      ctx.delete(name);
    });

    nextValueNames.forEach((name) => {
      ctx.delete(name);
    });

    // Then add the values from the previous row to the context.
    currentValueNames.forEach((name, idx) => {
      ctx.store(name, table[i - 1][idx]);
    });

    const row = [];

    for (let j = 0; j < nextValueFunctions.length; j++) {
      const nextValueFunction = nextValueFunctions[j];
      const nextValue = await nextValueFunction(ctx, []);
      row.push(nextValue);
      ctx.store(nextValueNames[j], nextValue);
    }

    table.push(row);
  }

  return table;
};

export default wrapFunction(DATASET, {
  name: 'DATASET',
  nargs: 5,
  types: ['number', 'string', 'any', 'string', 'function'],
  passContext: true,
});
